import { theme } from '@arnold/common';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { MetricBand } from '@arnold/core';
import { formatMetricNumber } from './formatMetricNumber';
import { localizedText } from './types';

/**
 * Bands are ordered best to worst: the first is good, the last needs attention, anything between is mixed.
 * A band's own `tone` wins, so segments like "at risk" stay red even when listed first.
 */
const bandColor = (band: MetricBand, index: number, total: number): string =>
  band.tone === 'good' || (!band.tone && index === 0)
    ? theme.colors.emotionSuccess.default
    : band.tone === 'bad' || index === total - 1
      ? theme.colors.emotionDanger.default
      : theme.colors.emotionWarning.default;

/**
 * Stacked share of respondents per band with a legend. Renders precomputed percentages only; it never
 * classifies respondents itself. `compact` drops the legend to a title for table cells.
 */
export function BandDistribution({
  bands,
  count,
  compact = false,
}: {
  bands: MetricBand[];
  count: number;
  compact?: boolean;
}) {
  const { i18n } = useTranslation('newReport');
  const label = (band: MetricBand) => localizedText(band.label, i18n.language) || band.id;
  const percent = (band: MetricBand) => `${formatMetricNumber(band.percentage, i18n.language)}%`;
  const summary = bands.map((band) => `${label(band)}: ${percent(band)} (${band.count})`).join(' · ');
  return (
    <Container>
      <Track title={summary} role="img" aria-label={summary}>
        {bands.map((band, index) => (
          <Segment
            key={band.id}
            style={{ width: `${band.percentage}%`, backgroundColor: bandColor(band, index, bands.length) }}
          />
        ))}
      </Track>
      {!compact && (
        <Legend>
          {bands.map((band, index) => (
            <LegendRow key={band.id}>
              <Swatch style={{ backgroundColor: bandColor(band, index, bands.length) }} />
              <LegendLabel>{label(band)}</LegendLabel>
              <LegendValue>{percent(band)}</LegendValue>
            </LegendRow>
          ))}
        </Legend>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.d};
  min-width: 0;
  width: 100%;
`;

const Track = styled.div`
  display: flex;
  min-width: 0;
  height: 10px;
  overflow: hidden;
  border-radius: 4px;
  background: ${theme.colors.backgroundBasic.active};
`;

const Segment = styled.div`
  height: 100%;
  flex-shrink: 0;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.c};
  padding: ${theme.spacing.c} 0;
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  font-size: ${theme.typography.body.small.regular.fontSize};
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 2px;
`;

const LegendLabel = styled.span`
  flex: 1;
  min-width: 0;
  color: ${theme.colors.text.primary};
`;

const LegendValue = styled.strong`
  flex-shrink: 0;
`;
